import { Link } from 'react-router-dom'

/**
 * Dead-number 404. Shares the visual language of PhoneStateCard — same
 * body gradient and dimensions — so it feels like a natural extension
 * of the app instead of a generic "oops" page. The screen shows a big
 * glitchy `404` instead of the usual clock, with cracks radiating out
 * from the center and a wandering CRT scanline. Frame shakes subtly
 * via `.notfound-glitch`; both effects honour `prefers-reduced-motion`
 * through the global guard in `src/index.css`.
 */
export function NotFoundPage() {
  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-gray-50 dark:bg-gray-950">
      <div className="flex flex-col items-center gap-6 max-w-md text-center">
        <div className="relative notfound-glitch">
          <DeadPhone />
          <div className="notfound-scanline" style={{ top: 0, bottom: 0 }} />
        </div>

        <div className="space-y-2">
          <p className="font-mono text-xs tracking-[0.25em] text-gray-400 dark:text-gray-500 uppercase">
            — пи&nbsp;— пи&nbsp;— пи —
          </p>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Эта страница не отвечает
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Похоже, вы набрали неправильный номер.
          </p>
        </div>

        <Link
          to="/"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Назад на главную
        </Link>
      </div>
    </div>
  )
}

/**
 * Standalone SVG — not reusing PhoneStateCard because the plan for that
 * component is a fixed four-state palette (normal/in-call/offline/ringing).
 * A 404 state would bloat its API for a one-off page. Copying 40 lines of
 * body shape is cheaper than wiring a fifth enum value.
 */
function DeadPhone() {
  return (
    <svg
      viewBox="0 0 240 360"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="404 — номер не найден"
      width={200}
      height={300}
      className="block"
    >
      <defs>
        <linearGradient id="nf-body" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#2a2f36" />
          <stop offset="1" stopColor="#15181d" />
        </linearGradient>
        <linearGradient id="nf-screen" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#1e1b4b" />
          <stop offset="0.5" stopColor="#312e81" />
          <stop offset="1" stopColor="#0f172a" />
        </linearGradient>
      </defs>

      {/* Body */}
      <rect x="20" y="10" width="200" height="340" rx="32" fill="url(#nf-body)" stroke="#0a0c0f" strokeWidth="2" />
      <rect x="218" y="90"  width="4" height="34" rx="2" fill="#0a0c0f" />
      <rect x="218" y="138" width="4" height="22" rx="2" fill="#0a0c0f" />
      {/* Screen */}
      <rect x="30" y="20" width="180" height="320" rx="26" fill="url(#nf-screen)" />
      {/* Camera LED — dead */}
      <circle cx="120" cy="36" r="5" fill="#0a0c0f" />
      <circle cx="120" cy="36" r="2" fill="#1f2937" />

      {/* Huge 404 with a duplicated cyan/magenta offset for channel-split vibe */}
      <g fontFamily="ui-monospace, 'SF Mono', Menlo, monospace" fontWeight="700" fontSize="84" textAnchor="middle">
        <text x="122" y="198" fill="#ef4444" opacity="0.55">404</text>
        <text x="118" y="198" fill="#38bdf8" opacity="0.55">404</text>
        <text x="120" y="198" fill="#ffffff">404</text>
      </g>
      <text x="120" y="228" textAnchor="middle" fontFamily="ui-monospace, monospace" fontSize="10" fill="#ffffff" opacity="0.6" letterSpacing="3">
        NO SIGNAL
      </text>

      {/* Crack lines radiating from center — hand-placed polylines */}
      <g stroke="#ffffff" strokeWidth="1.2" fill="none" opacity="0.45" strokeLinecap="round">
        <polyline points="120,180 96,120 70,96 54,60" />
        <polyline points="120,180 150,112 182,88" />
        <polyline points="120,180 88,238 58,268" />
        <polyline points="120,180 162,250 200,280" />
        <polyline points="120,180 120,260 108,312" />
      </g>
      {/* Small crack spurs */}
      <g stroke="#ffffff" strokeWidth="0.8" fill="none" opacity="0.3" strokeLinecap="round">
        <polyline points="96,120 110,130" />
        <polyline points="150,112 140,128" />
        <polyline points="88,238 106,232" />
        <polyline points="162,250 152,240" />
      </g>

      {/* Dead-signal strip — alternating bars at the top */}
      <g fill="#ffffff" opacity="0.2">
        <rect x="50"  y="42" width="8" height="4" />
        <rect x="62"  y="42" width="4" height="4" />
        <rect x="72"  y="42" width="10" height="4" />
        <rect x="86"  y="42" width="6" height="4" />
        <rect x="96"  y="42" width="4" height="4" />
      </g>

      {/* Bottom "home" bar, faded */}
      <rect x="92" y="328" width="56" height="4" rx="2" fill="#ffffff" opacity="0.25" />
    </svg>
  )
}
